import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Heart, X, MapPin, GraduationCap, Briefcase, ShieldCheck, ChevronDown, ChevronUp,
  SlidersHorizontal, User, Calendar, Ruler, Eye, MessageCircle, Bookmark, Info
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain'];
const DIETS = ['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan'];

function SwipeCard({ profile, onSwipeLeft, onSwipeRight, onViewProfile }) {
  const cardRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [swiping, setSwiping] = useState(null); // 'left' | 'right' | null
  const [exiting, setExiting] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const calcAge = (dob) => {
    if (!dob) return '';
    return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const age = calcAge(profile.dob);

  const handleStart = (clientX) => {
    startX.current = clientX;
    currentX.current = clientX;
  };

  const handleMove = (clientX) => {
    currentX.current = clientX;
    const diff = clientX - startX.current;
    setDragOffset(diff);
    if (diff > 50) setSwiping('right');
    else if (diff < -50) setSwiping('left');
    else setSwiping(null);
  };

  const handleEnd = () => {
    const diff = currentX.current - startX.current;
    if (diff > 120) {
      triggerSwipe('right');
    } else if (diff < -120) {
      triggerSwipe('left');
    } else {
      setDragOffset(0);
      setSwiping(null);
    }
  };

  const triggerSwipe = (direction) => {
    setExiting(direction);
    setTimeout(() => {
      if (direction === 'right') onSwipeRight();
      else onSwipeLeft();
    }, 300);
  };

  const rotation = dragOffset * 0.08;
  const opacity = Math.max(0, 1 - Math.abs(dragOffset) / 400);

  return (
    <div
      ref={cardRef}
      className={`absolute inset-0 touch-none ${exiting ? 'transition-transform duration-300' : ''}`}
      style={{
        transform: exiting
          ? `translateX(${exiting === 'right' ? '120%' : '-120%'}) rotate(${exiting === 'right' ? 15 : -15}deg)`
          : `translateX(${dragOffset}px) rotate(${rotation}deg)`,
        opacity: exiting ? 0 : opacity,
      }}
      onTouchStart={(e) => handleStart(e.touches[0].clientX)}
      onTouchMove={(e) => handleMove(e.touches[0].clientX)}
      onTouchEnd={handleEnd}
      onMouseDown={(e) => { e.preventDefault(); handleStart(e.clientX); document.addEventListener('mousemove', onMouseMove); document.addEventListener('mouseup', onMouseUp); }}
      data-testid="swipe-card"
    >
      {/* Swipe indicators */}
      {swiping === 'right' && (
        <div className="absolute top-6 left-6 z-20 rotate-[-15deg] border-4 border-green-500 rounded-xl px-4 py-2 bg-green-500/20 backdrop-blur-sm">
          <span className="text-green-500 font-black text-2xl tracking-wider">LIKE</span>
        </div>
      )}
      {swiping === 'left' && (
        <div className="absolute top-6 right-6 z-20 rotate-[15deg] border-4 border-red-500 rounded-xl px-4 py-2 bg-red-500/20 backdrop-blur-sm">
          <span className="text-red-500 font-black text-2xl tracking-wider">PASS</span>
        </div>
      )}

      <Card className="w-full h-full rounded-3xl overflow-hidden border-0 shadow-xl bg-white flex flex-col">
        {/* Photo section */}
        <div className="relative flex-1 min-h-0 bg-gradient-to-br from-[#2D2424] to-[#1a1414]">
          {profile.photos?.length > 0 ? (
            <img src={profile.photos[0]} alt={profile.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#D9381E]/30 to-[#F2A900]/30 flex items-center justify-center">
                <span className="text-6xl font-bold text-white/40">{profile.name?.[0]}</span>
              </div>
            </div>
          )}
          {/* Gradient overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            {profile.verified_badge && (
              <Badge className="bg-green-500/90 text-white text-xs gap-1 backdrop-blur-sm">
                <ShieldCheck className="w-3 h-3" /> Verified
              </Badge>
            )}
            {profile.subscription?.plan && profile.subscription.plan !== 'free' && (
              <Badge className="bg-[#F2A900]/90 text-[#2D2424] text-xs backdrop-blur-sm">Premium</Badge>
            )}
          </div>

          {/* Profile info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
            <h2 className="text-2xl font-bold leading-tight">
              {profile.name}{age ? `, ${age}` : ''}
            </h2>
            <div className="flex items-center gap-3 mt-1.5 text-sm text-white/80">
              {profile.city && (
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {profile.city}</span>
              )}
              {profile.height > 0 && (
                <span className="flex items-center gap-1"><Ruler className="w-3.5 h-3.5" /> {profile.height} cm</span>
              )}
            </div>
            {profile.education_degree && (
              <div className="flex items-center gap-1 mt-1 text-sm text-white/70">
                <GraduationCap className="w-3.5 h-3.5" /> {profile.education_degree}
                {profile.occupation && ` | ${profile.occupation}`}
              </div>
            )}
          </div>
        </div>

        {/* Expandable details */}
        <div className="bg-white">
          <button
            onClick={(e) => { e.stopPropagation(); setShowDetails(!showDetails); }}
            className="w-full flex items-center justify-center gap-1 py-2 text-xs text-[#78716C] hover:text-[#2D2424] transition-colors"
            data-testid="toggle-card-details"
          >
            {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            {showDetails ? 'Less' : 'More details'}
          </button>
          {showDetails && (
            <div className="px-5 pb-4 space-y-2 text-sm border-t border-[#F5F1EB] pt-3 max-h-48 overflow-y-auto" data-testid="card-details-expanded">
              {profile.religion && (
                <div className="flex items-center gap-2 text-[#78716C]">
                  <Eye className="w-3.5 h-3.5 text-[#F2A900]" />
                  <span>{profile.religion}{profile.caste ? ` - ${profile.caste}` : ''}</span>
                </div>
              )}
              {profile.income && (
                <div className="flex items-center gap-2 text-[#78716C]">
                  <Briefcase className="w-3.5 h-3.5 text-[#F2A900]" />
                  <span>{profile.income}</span>
                </div>
              )}
              {profile.diet && (
                <div className="flex items-center gap-2 text-[#78716C]">
                  <span className="w-3.5 h-3.5 text-center text-[#F2A900] text-xs">🍽</span>
                  <span>{profile.diet}</span>
                </div>
              )}
              {profile.bio && (
                <p className="text-[#78716C] text-xs leading-relaxed mt-2 line-clamp-3">{profile.bio}</p>
              )}
              <Button
                variant="ghost" size="sm"
                onClick={(e) => { e.stopPropagation(); onViewProfile(); }}
                className="w-full mt-2 text-[#D9381E] hover:bg-[#D9381E]/5 rounded-full text-xs"
                data-testid="view-full-profile-btn"
              >
                <Info className="w-3 h-3 mr-1" /> View Full Profile
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  function onMouseMove(e) { handleMove(e.clientX); }
  function onMouseUp() {
    handleEnd();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }
}

export default function DashboardPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({ liked: 0, passed: 0 });
  const [filters, setFilters] = useState({
    gender: user?.gender === 'male' ? 'female' : 'male',
    religion: '', caste: '', city: '',
    age_min: '', age_max: '', diet: '',
    verified_only: false,
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchProfiles = useCallback(async (pg = 1, append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filtersRef.current).forEach(([k, v]) => {
        if (v && v !== '' && v !== false) params.append(k, v);
      });
      params.append('page', pg);
      params.append('limit', 20);
      const res = await api.get(`/profiles/search/all?${params}`);
      const newProfiles = res.data.profiles || [];
      if (append) {
        setProfiles(prev => [...prev, ...newProfiles]);
      } else {
        setProfiles(newProfiles);
        setCurrentIdx(0);
      }
      setHasMore(newProfiles.length >= 20);
      setPage(pg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => { if (user) fetchProfiles(); }, [user, fetchProfiles]);

  // Load more when running low
  useEffect(() => {
    if (currentIdx >= profiles.length - 3 && hasMore && !loading) {
      fetchProfiles(page + 1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx]);

  const handleSwipeRight = async () => {
    const profile = profiles[currentIdx];
    if (profile) {
      try {
        await api.post('/interests', { to_user_id: profile.id });
        toast.success(`Interest sent to ${profile.name}!`, { duration: 1500 });
      } catch (e) {
        // Silently handle duplicates
        if (!e.response?.data?.detail?.includes('already')) {
          toast.error(e.response?.data?.detail || 'Could not send interest');
        }
      }
      setStats(s => ({ ...s, liked: s.liked + 1 }));
    }
    setCurrentIdx(i => i + 1);
  };

  const handleSwipeLeft = () => {
    setStats(s => ({ ...s, passed: s.passed + 1 }));
    setCurrentIdx(i => i + 1);
  };

  const handleViewProfile = () => {
    const profile = profiles[currentIdx];
    if (profile) navigate(`/profile/${profile.id}`);
  };

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const applyFilters = () => { setShowFilters(false); fetchProfiles(1); };
  const clearFilters = () => {
    const def = { gender: user?.gender === 'male' ? 'female' : 'male', religion: '', caste: '', city: '', age_min: '', age_max: '', diet: '', verified_only: false };
    setFilters(def);
    setTimeout(() => fetchProfiles(1), 0);
  };

  const currentProfile = profiles[currentIdx];
  const isExhausted = !loading && currentIdx >= profiles.length;

  return (
    <div className="max-w-lg mx-auto px-4 py-4 min-h-[calc(100vh-64px)] flex flex-col" data-testid="dashboard-page">
      {/* Filter Bar */}
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-lg font-bold text-[#2D2424]">Discover</h1>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-3 text-xs text-[#78716C]">
            <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-green-500 fill-green-500" /> {stats.liked}</span>
            <span className="flex items-center gap-1"><X className="w-3 h-3 text-red-400" /> {stats.passed}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`rounded-full gap-1.5 text-xs h-8 ${showFilters ? 'bg-[#D9381E] text-white border-[#D9381E]' : 'border-[#E7E5E4]'}`}
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
          </Button>
        </div>
      </div>

      {/* Collapsible Filters */}
      {showFilters && (
        <Card className="p-4 rounded-2xl border border-[#E7E5E4] bg-white mb-3 animate-in slide-in-from-top-2 duration-200" data-testid="filter-panel">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-[#78716C]">Gender</Label>
              <Select value={filters.gender} onValueChange={(v) => updateFilter('gender', v)}>
                <SelectTrigger className="mt-1 h-9 text-sm rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#78716C]">Religion</Label>
              <Select value={filters.religion} onValueChange={(v) => updateFilter('religion', v)}>
                <SelectTrigger className="mt-1 h-9 text-sm rounded-lg"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any_religion">Any</SelectItem>
                  {RELIGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#78716C]">Age Min</Label>
              <Input type="number" value={filters.age_min} onChange={(e) => updateFilter('age_min', e.target.value)} placeholder="18" className="mt-1 h-9 text-sm rounded-lg" data-testid="filter-age-min" />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#78716C]">Age Max</Label>
              <Input type="number" value={filters.age_max} onChange={(e) => updateFilter('age_max', e.target.value)} placeholder="40" className="mt-1 h-9 text-sm rounded-lg" data-testid="filter-age-max" />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#78716C]">City</Label>
              <Input value={filters.city} onChange={(e) => updateFilter('city', e.target.value)} placeholder="Any" className="mt-1 h-9 text-sm rounded-lg" data-testid="filter-city" />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#78716C]">Diet</Label>
              <Select value={filters.diet} onValueChange={(v) => updateFilter('diet', v)}>
                <SelectTrigger className="mt-1 h-9 text-sm rounded-lg"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any_diet">Any</SelectItem>
                  {DIETS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between col-span-2">
              <Label className="text-xs font-medium text-[#78716C]">Verified Only</Label>
              <Switch checked={filters.verified_only} onCheckedChange={(v) => updateFilter('verified_only', v)} data-testid="filter-verified" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={applyFilters} size="sm" className="flex-1 bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full h-9 text-xs" data-testid="apply-filters-btn">
              Apply Filters
            </Button>
            <Button onClick={() => { clearFilters(); }} variant="outline" size="sm" className="rounded-full h-9 text-xs border-[#E7E5E4]" data-testid="clear-filters-btn">
              Clear
            </Button>
          </div>
        </Card>
      )}

      {/* Swipe Area */}
      <div className="flex-1 flex flex-col items-center justify-center min-h-0">
        {loading && profiles.length === 0 ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-3 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-[#78716C]">Finding matches...</p>
          </div>
        ) : isExhausted ? (
          <Card className="p-10 text-center rounded-3xl border border-[#E7E5E4] w-full max-w-sm" data-testid="no-more-profiles">
            <User className="w-16 h-16 text-[#E7E5E4] mx-auto mb-4" />
            <h3 className="text-lg font-bold text-[#2D2424] mb-2">No more profiles</h3>
            <p className="text-sm text-[#78716C] mb-4">You've seen all available profiles. Try adjusting your filters or check back later.</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => { setCurrentIdx(0); fetchProfiles(1); }} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full px-6" data-testid="refresh-profiles-btn">
                Refresh
              </Button>
              <Button variant="outline" onClick={() => setShowFilters(true)} className="rounded-full border-[#E7E5E4]">
                <SlidersHorizontal className="w-4 h-4 mr-1" /> Filters
              </Button>
            </div>
          </Card>
        ) : currentProfile ? (
          <div className="relative w-full max-w-sm" style={{ height: 'calc(100vh - 220px)', maxHeight: '600px' }}>
            {/* Next card preview (behind current) */}
            {profiles[currentIdx + 1] && (
              <div className="absolute inset-0 scale-[0.95] opacity-50" style={{ transform: 'scale(0.95) translateY(8px)' }}>
                <Card className="w-full h-full rounded-3xl overflow-hidden border-0 shadow-lg bg-[#F5F1EB]" />
              </div>
            )}
            {/* Current card */}
            <SwipeCard
              key={currentProfile.id}
              profile={currentProfile}
              onSwipeLeft={handleSwipeLeft}
              onSwipeRight={handleSwipeRight}
              onViewProfile={handleViewProfile}
            />
          </div>
        ) : null}
      </div>

      {/* Action Buttons */}
      {currentProfile && !isExhausted && (
        <div className="flex items-center justify-center gap-4 py-4" data-testid="swipe-actions">
          <button
            onClick={handleSwipeLeft}
            className="w-14 h-14 rounded-full bg-white border-2 border-red-300 flex items-center justify-center shadow-lg hover:scale-110 hover:border-red-500 transition-all active:scale-95"
            data-testid="swipe-left-btn"
          >
            <X className="w-7 h-7 text-red-500" />
          </button>
          <button
            onClick={handleViewProfile}
            className="w-11 h-11 rounded-full bg-white border-2 border-[#78716C]/30 flex items-center justify-center shadow-md hover:scale-110 transition-all active:scale-95"
            data-testid="view-profile-btn"
          >
            <Info className="w-5 h-5 text-[#78716C]" />
          </button>
          <button
            onClick={handleSwipeRight}
            className="w-14 h-14 rounded-full bg-white border-2 border-green-300 flex items-center justify-center shadow-lg hover:scale-110 hover:border-green-500 transition-all active:scale-95"
            data-testid="swipe-right-btn"
          >
            <Heart className="w-7 h-7 text-green-500" />
          </button>
        </div>
      )}
    </div>
  );
}
